import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import CrisisResourceBanner from '../../components/CrisisResourceBanner';
import SessionStatusIndicator from '../../components/SessionStatusIndicator';
import QuickBookingWidget from '../../components/QuickBookingWidget';
import UpcomingAppointments from './components/UpcomingAppointments';
import SessionHistory from './components/SessionHistory';
import WellbeingTracker from './components/WellbeingTracker';
import EmergencyResources from './components/EmergencyResources';
import ResourceLibrary from './components/ResourceLibrary';
import DashboardStatsCard from './components/DashboardStatsCard';
import QuickActionsFooter from './components/QuickActionsFooter';
import Icon from '../../components/AppIcon';
import { useDashboardData } from './hooks/useDashboardData';

const PersonalDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const { appointments, wellbeingScore, loading, error, stats, loadDashboardData } = useDashboardData();

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'appointments', label: 'Appointments', icon: 'Calendar' },
    { id: 'history', label: 'History', icon: 'History' },
    { id: 'wellbeing', label: 'Wellbeing', icon: 'Heart' },
    { id: 'resources', label: 'Resources', icon: 'Library' },
    { id: 'emergency', label: 'Emergency', icon: 'AlertCircle' }
  ];

  const statsData = [
    {
      id: 1,
      label: 'Total Sessions',
      value: stats?.totalSessions,
      change: '+3 this month',
      icon: 'Video',
      color: 'text-primary'
    },
    {
      id: 2,
      label: 'Upcoming',
      value: stats?.upcomingSessions,
      change: 'Next in 53 min',
      icon: 'Calendar',
      color: 'text-warning'
    },
    {
      id: 3,
      label: 'Avg. Rating',
      value: stats?.averageRating,
      change: 'Out of 5.0',
      icon: 'Star',
      color: 'text-success'
    },
    {
      id: 4,
      label: 'Wellness Score',
      value: `${stats?.wellnessScore || 0}%`,
      change: '+5% this week',
      icon: 'TrendingUp',
      color: 'text-primary'
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <UpcomingAppointments />
              <SessionHistory />
            </div>
            <div className="space-y-6">
              <QuickBookingWidget />
              <WellbeingTracker />
            </div>
          </div>
        );
      case 'appointments':
        return <UpcomingAppointments />;
      case 'history':
        return <SessionHistory />;
      case 'wellbeing':
        return <WellbeingTracker />;
      case 'resources':
        return <ResourceLibrary />;
      case 'emergency':
        return <EmergencyResources />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="mt-4 text-gray-600">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <p className="text-error mb-4">{error}</p>
            <button 
              onClick={loadDashboardData}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Personal Dashboard - Cwtch Platform</title>
        <meta name="description" content="Manage your mental health journey with appointment tracking, session history, and wellbeing monitoring tools." />
      </Helmet>
      <CrisisResourceBanner />
      <Header />
      <div className="min-h-screen bg-background pt-32">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">Welcome back, Sarah</h1>
            <p className="text-muted-foreground">Here's an overview of your mental health journey</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {statsData?.map((stat) => (
              <DashboardStatsCard key={stat?.id} stat={stat} />
            ))}
          </div>

          <div className="mb-6 overflow-x-auto">
            <div className="flex items-center gap-2 min-w-max">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                    activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-card text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="mb-8">
            {renderTabContent()}
          </div>

          <QuickActionsFooter
            onCallCrisis={() => window.location.href = 'tel:988'}
            onViewResources={() => setActiveTab('emergency')}
          />
        </div>
      </div>
      <SessionStatusIndicator
        sessionActive={false}
        sessionData={null}
      />
    </>
  );
};

export default PersonalDashboard;